import { useState } from "react";
import Header from "../components/site/Header";
import Footer from "../components/site/Footer";
import PageHero from "../components/site/PageHero";
import { Field, FileField, SuccessPanel, useEnquiryForm, inputCls } from "../components/forms/fields";
import { FadeUp } from "../components/site/Reveal";

const INITIAL = {
    counterpartyRole: "",
    fullName: "",
    position: "",
    companyName: "",
    companyWebsite: "",
    registrationCountry: "",
    businessEmail: "",
    phone: "",
    productCategory: "",
    exactProduct: "",
    specification: "",
    quantity: "",
    deliveryFrequency: "",
    productOrigin: "",
    destination: "",
    incoterm: "",
    targetTimeline: "",
    agreement: false,
};

export default function TradingEnquiry() {
    const [form, setForm] = useState(INITIAL);
    const [file, setFile] = useState(null);
    const { submitting, reference, submit } = useEnquiryForm("trading");
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

    const onSubmit = (e) => {
        e.preventDefault();
        const { agreement, ...data } = form;
        submit(data, file, agreement);
    };

    return (
        <div className="grain bg-navy-deep" data-testid="trading-enquiry-page">
            <Header />
            <main>
                <PageHero
                    seo={{
                        title: "Trading Enquiry | Buyers, Suppliers & Partners — Viet Medica Ltd",
                        description:
                            "Submit a trading requirement — energy, rice, dry fruits, nuts or seafood. Viet Medica Ltd routes your enquiry to the appropriate commercial team.",
                        path: "/trading-enquiry",
                    }}
                    crumbs={[{ label: "International Trading", href: "/international-trading" }, { label: "Trading Enquiry" }]}
                    eyebrow="International Trading"
                    title="Trading"
                    titleEm="Enquiry."
                    copy="Provide clear product and company details. All trading enquiries are subject to counterparty and compliance verification before introductions proceed."
                />

                <section className="bg-navy-deep py-16 lg:py-24">
                    <div className="mx-auto max-w-3xl px-5 lg:px-10">
                        {reference ? (
                            <SuccessPanel reference={reference} categoryLabel="trading" />
                        ) : (
                            <FadeUp>
                                <form onSubmit={onSubmit} className="space-y-6 border border-white/10 bg-navy-surface p-7 lg:p-10" data-testid="trading-enquiry-form">
                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                        <Field label="I am a" htmlFor="td-role" required>
                                            <select id="td-role" data-testid="td-role" required className={inputCls} value={form.counterpartyRole} onChange={set("counterpartyRole")}>
                                                <option value="">Select…</option>
                                                <option>Buyer</option>
                                                <option>Seller</option>
                                                <option>Mandate</option>
                                                <option>Distributor</option>
                                                <option>Partner</option>
                                            </select>
                                        </Field>
                                        <Field label="Full Name and Position" htmlFor="td-name" required>
                                            <input id="td-name" data-testid="td-name" required className={inputCls} value={form.fullName} onChange={set("fullName")} placeholder="Name — Position" />
                                        </Field>
                                        <Field label="Position" htmlFor="td-position">
                                            <input id="td-position" data-testid="td-position" className={inputCls} value={form.position} onChange={set("position")} />
                                        </Field>
                                        <Field label="Registered Company Name" htmlFor="td-company" required>
                                            <input id="td-company" data-testid="td-company" required className={inputCls} value={form.companyName} onChange={set("companyName")} />
                                        </Field>
                                        <Field label="Company Website" htmlFor="td-website">
                                            <input id="td-website" data-testid="td-website" type="url" className={inputCls} value={form.companyWebsite} onChange={set("companyWebsite")} placeholder="https://" />
                                        </Field>
                                        <Field label="Registration Country" htmlFor="td-regcountry" required>
                                            <input id="td-regcountry" data-testid="td-regcountry" required className={inputCls} value={form.registrationCountry} onChange={set("registrationCountry")} />
                                        </Field>
                                        <Field label="Business Email" htmlFor="td-email" required>
                                            <input id="td-email" data-testid="td-email" required type="email" className={inputCls} value={form.businessEmail} onChange={set("businessEmail")} />
                                        </Field>
                                        <Field label="Telephone or WhatsApp" htmlFor="td-phone" required>
                                            <input id="td-phone" data-testid="td-phone" required type="tel" className={inputCls} value={form.phone} onChange={set("phone")} />
                                        </Field>
                                        <Field label="Product Category" htmlFor="td-category" required>
                                            <select id="td-category" data-testid="td-category" required className={inputCls} value={form.productCategory} onChange={set("productCategory")}>
                                                <option value="">Select…</option>
                                                <option>Oil &amp; Gas Products</option>
                                                <option>Rice</option>
                                                <option>Dry Fruits &amp; Nuts</option>
                                                <option>Seafood</option>
                                                <option>Other</option>
                                            </select>
                                        </Field>
                                        <Field label="Exact Product" htmlFor="td-product" required>
                                            <input id="td-product" data-testid="td-product" required className={inputCls} value={form.exactProduct} onChange={set("exactProduct")} />
                                        </Field>
                                        <Field label="Specification or Grade" htmlFor="td-spec">
                                            <input id="td-spec" data-testid="td-spec" className={inputCls} value={form.specification} onChange={set("specification")} />
                                        </Field>
                                        <Field label="Required or Available Quantity" htmlFor="td-quantity" required>
                                            <input id="td-quantity" data-testid="td-quantity" required className={inputCls} value={form.quantity} onChange={set("quantity")} />
                                        </Field>
                                        <Field label="Delivery Frequency" htmlFor="td-frequency">
                                            <input id="td-frequency" data-testid="td-frequency" className={inputCls} value={form.deliveryFrequency} onChange={set("deliveryFrequency")} placeholder="e.g. Monthly" />
                                        </Field>
                                        <Field label="Product Origin" htmlFor="td-origin">
                                            <input id="td-origin" data-testid="td-origin" className={inputCls} value={form.productOrigin} onChange={set("productOrigin")} />
                                        </Field>
                                        <Field label="Destination Country and Port" htmlFor="td-destination">
                                            <input id="td-destination" data-testid="td-destination" className={inputCls} value={form.destination} onChange={set("destination")} />
                                        </Field>
                                        <Field label="Preferred Incoterm" htmlFor="td-incoterm">
                                            <select id="td-incoterm" data-testid="td-incoterm" className={inputCls} value={form.incoterm} onChange={set("incoterm")}>
                                                <option value="">Select…</option>
                                                {["EXW", "FOB", "CFR", "CIF", "DAP", "DDP", "Other"].map((i) => (
                                                    <option key={i}>{i}</option>
                                                ))}
                                            </select>
                                        </Field>
                                        <Field label="Target Timeline" htmlFor="td-timeline">
                                            <input id="td-timeline" data-testid="td-timeline" className={inputCls} value={form.targetTimeline} onChange={set("targetTimeline")} />
                                        </Field>
                                    </div>
                                    <Field label="Supporting Document" htmlFor="td-file" hint="Optional. PDF, PNG or JPG, max 3 MB — e.g. company profile or LOI.">
                                        <FileField id="td-file" file={file} onChange={setFile} />
                                    </Field>
                                    <label htmlFor="td-agreement" className="flex items-start gap-3 text-sm text-slate-300">
                                        <input id="td-agreement" data-testid="td-agreement" required type="checkbox" checked={form.agreement} onChange={set("agreement")} className="mt-1 h-4 w-4 accent-[#C5A059]" />
                                        I agree to counterparty and compliance verification before any introduction proceeds.
                                    </label>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="td-submit"
                                        className="w-full bg-gold px-6 py-4 text-xs font-bold uppercase tracking-[0.2em] text-navy-deep transition-colors hover:bg-gold-bright disabled:opacity-50"
                                    >
                                        {submitting ? "Sending…" : "Send Trading Enquiry"}
                                    </button>
                                </form>
                            </FadeUp>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
